import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useShop } from "@/contexts/ShopContext";
import Modal from "@/components/Modal";
import { toast } from "sonner";

export default function ShopSetup() {
  const { user } = useAuth();
  const { currentShop, shops, reloadShops } = useShop();
  const [profile, setProfile] = useState({ name: "", address: "", contact_number: "", working_hours: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [shopForm, setShopForm] = useState({ name: "", address: "", contact_number: "", working_hours: "", shop_admin_name: "", shop_admin_email: "", shop_admin_password: "" });

  useEffect(() => {
    if (currentShop) {
      setProfile({ name: currentShop.name, address: currentShop.address || "", contact_number: currentShop.contact_number || "", working_hours: currentShop.working_hours || "" });
    }
  }, [currentShop]);

  const saveProfile = async () => {
    await api.patch(`/shops/${currentShop.id}`, profile);
    toast.success("Shop profile saved");
    reloadShops();
  };

  const createShop = async () => {
    if (!shopForm.name.trim() || !shopForm.shop_admin_email.trim() || !shopForm.shop_admin_password) {
      toast.error("Shop name and shop admin credentials are required"); return;
    }
    try {
      await api.post("/shops", shopForm);
      toast.success("Shop created");
      setModalOpen(false);
      setShopForm({ name: "", address: "", contact_number: "", working_hours: "", shop_admin_name: "", shop_admin_email: "", shop_admin_password: "" });
      reloadShops();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create shop");
    }
  };

  const toggleShopActive = async (shop) => {
    await api.patch(`/shops/${shop.id}`, { is_active: !shop.is_active });
    reloadShops();
  };

  return (
    <section className="view" data-testid="shop-setup-page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Shop profile</div>
          <h1>Shop Setup</h1>
          <p>Shop Admins edit their own shop; Admins manage every shop in their tenant.</p>
        </div>
      </div>

      {currentShop && (
        <div className="card">
          <div className="section-head"><div><h2>Your shop profile</h2><p>Changes reflect immediately on Online Order</p></div></div>
          <div className="field"><label className="field-label">Shop name</label><input className="input" style={{ width: "100%" }} value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="shop-name-input" /></div>
          <div className="field" style={{ marginTop: 12 }}><label className="field-label">Address</label><input className="input" style={{ width: "100%" }} value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} data-testid="shop-address-input" /></div>
          <div className="field" style={{ marginTop: 12 }}><label className="field-label">Contact number</label><input className="input" style={{ width: "100%" }} value={profile.contact_number} onChange={(e) => setProfile({ ...profile, contact_number: e.target.value })} data-testid="shop-contact-input" /></div>
          <div className="field" style={{ marginTop: 12 }}><label className="field-label">Working hours</label><input className="input" style={{ width: "100%" }} value={profile.working_hours} onChange={(e) => setProfile({ ...profile, working_hours: e.target.value })} data-testid="shop-hours-input" /></div>
          <div style={{ padding: "16px 20px" }}><button className="btn btn-primary" onClick={saveProfile} data-testid="save-shop-profile-btn">✓ Save changes</button></div>
        </div>
      )}

      {(user.role === "admin" || user.role === "superadmin") && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="section-head">
            <div><h2>All shops</h2><p>Only an Admin can create shops and assign a Shop Admin to each</p></div>
            {user.role === "admin" && <button className="btn btn-secondary btn-sm" onClick={() => setModalOpen(true)} data-testid="add-shop-btn">＋ Add shop</button>}
          </div>
          <div className="queue-grid">
            {shops.map((s) => (
              <div className="card queue-card" key={s.id} data-testid={`shop-card-${s.id}`}>
                <div className="queue-top">
                  <div><strong>{s.name}</strong><small>Shop Admin: {s.shop_admin_name || "—"}</small></div>
                  <span className={`status ${s.is_active ? "delivered" : "cancelled"}`}>{s.is_active ? "Open" : "Closed"}</span>
                </div>
                <div className="queue-footer">
                  <button className="btn btn-secondary" onClick={() => toggleShopActive(s)} data-testid={`toggle-shop-${s.id}`}>{s.is_active ? "Deactivate" : "Activate"}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add shop"
        footer={<button className="btn btn-primary" onClick={createShop} data-testid="create-shop-submit-btn">Create shop</button>}>
        <div><label className="field-label">Shop name</label><input className="input" style={{ width: "100%" }} value={shopForm.name} onChange={(e) => setShopForm({ ...shopForm, name: e.target.value })} data-testid="new-shop-name-input" /></div>
        <div><label className="field-label">Address</label><input className="input" style={{ width: "100%" }} value={shopForm.address} onChange={(e) => setShopForm({ ...shopForm, address: e.target.value })} data-testid="new-shop-address-input" /></div>
        <div><label className="field-label">Shop admin name</label><input className="input" style={{ width: "100%" }} value={shopForm.shop_admin_name} onChange={(e) => setShopForm({ ...shopForm, shop_admin_name: e.target.value })} data-testid="new-shop-admin-name-input" /></div>
        <div><label className="field-label">Shop admin email</label><input type="email" className="input" style={{ width: "100%" }} value={shopForm.shop_admin_email} onChange={(e) => setShopForm({ ...shopForm, shop_admin_email: e.target.value })} data-testid="new-shop-admin-email-input" /></div>
        <div><label className="field-label">Shop admin password</label><input type="password" className="input" style={{ width: "100%" }} value={shopForm.shop_admin_password} onChange={(e) => setShopForm({ ...shopForm, shop_admin_password: e.target.value })} data-testid="new-shop-admin-password-input" /></div>
      </Modal>
    </section>
  );
}